import { APP_ROUTE_PATTERNS } from '../seo/routes.js';

/**
 * The label Google Analytics is told a page view happened on.
 *
 * **Never the raw URL.** Several of this app's URLs are credentials. The
 * unsubscribe link carries a user id and a bearer token in its query
 * (`/unsubscribe?u=…&t=…`); `/invite/:token` is a capability in a path
 * segment; and CLAUDE.md records that the unauthenticated screen endpoint
 * makes a `quizId` effectively the results key. Handing any of those to a
 * third party would put a working credential in someone else's logs and in
 * every GA report that lists page paths.
 *
 * So a concrete path is reported as the *route pattern* it matched —
 * `/polls/abc123/results` is counted as `/polls/:id/results` — and the
 * patterns come from `APP_ROUTE_PATTERNS`, the table the edge already uses to
 * decide a path exists. Deriving beats a second list: that table is held to
 * App.tsx by `routes.appRoutes.test.ts`, so a route added later is collapsed
 * here the day it ships rather than the day someone remembers.
 *
 * A path matching nothing reports as `(unmatched)`. That is the fail-safe
 * direction and the reason this is an allowlist rather than a denylist of
 * known-secret routes: a URL shape nobody anticipated is counted without being
 * described, instead of being described in full because no rule named it.
 */

/** `^/polls/[^/]+/results$` reads as `/polls/:id/results` in a report. */
function label(patternSource: string): string {
  return patternSource
    .replace(/^\^/, '')
    .replace(/\$$/, '')
    .replace(/\(\/\.\*\)\?/g, '/*')
    .replace(/\[\^\/\]\+/g, ':id');
}

const ROUTES = APP_ROUTE_PATTERNS.map((source) => ({
  match: new RegExp(source),
  label: label(source),
}));

/**
 * Query parameters allowed through, because neither describes a person.
 *
 * `via` is a channel label — signupSource.ts puts it in the URL for exactly
 * that reason, having reasoned that a slug describes the page someone came
 * from and not the person. `billing` is the literal `success` or `cancel` a
 * Stripe return carries. Every other parameter is dropped unread rather than
 * judged, which is what keeps `u` and `t` out.
 */
const SAFE_PARAMS = ['via', 'billing'];

export function pagePath(pathname: string, search = ''): string {
  const matched = ROUTES.find((route) => route.match.test(pathname));
  const base = matched ? matched.label : '(unmatched)';

  const kept = new URLSearchParams();
  const incoming = new URLSearchParams(search);
  for (const name of SAFE_PARAMS) {
    const value = incoming.get(name);
    if (value !== null) kept.set(name, value);
  }

  const query = kept.toString();
  return query ? `${base}?${query}` : base;
}
